// A post template: the form of a post someone else made, in our words, never their words.
// Inspiration derives one from a real post's picture, title, hashtags and duration; Create
// follows it while the property's own photos, reviews and facts supply everything seen and
// said. Every field is a closed set or a short line, so nothing of the source can travel.
import { z } from 'zod';
import { POST_SHAPES } from './shapes.js';

export const TEMPLATE_HOOKS = [
  'question',
  'number',
  'pov',
  'price',
  'dare',
  'story',
  'reveal',
  'none',
] as const;
export const TEMPLATE_CAMERAS = [
  'pov-walkthrough',
  'one-reveal',
  'drone',
  'static-frame',
  'timelapse',
  'split-screen',
  'listicle',
] as const;
export const TEMPLATE_SUBJECTS = [
  'hot-tub',
  'view',
  'bed',
  'fireplace',
  'pool',
  'exterior',
  'kitchen',
  'dock',
  'bath',
  'deck',
  'game-room',
  'other',
] as const;
export const TEMPLATE_LIGHTS = [
  'golden-hour',
  'night',
  'snow',
  'overcast',
  'bright-day',
  'interior-warm',
  'unknown',
] as const;
export const TEMPLATE_MOODS = [
  'cozy',
  'luxury',
  'playful',
  'cinematic',
  'calm',
  'adventurous',
  'romantic',
  'family',
] as const;
export const TEMPLATE_TEXT = ['none', 'one-line', 'list', 'price', 'question'] as const;
export const TEMPLATE_TEXT_POSITIONS = ['top', 'center', 'bottom'] as const;
export const TEMPLATE_LENGTHS = ['under-15s', '15-30s', '30-60s', 'over-60s', 'unknown'] as const;
export const TEMPLATE_TEMPOS = ['slow', 'mid', 'fast'] as const;
export const TEMPLATE_ENERGIES = ['low', 'mid', 'high'] as const;
export const TEMPLATE_SOUND_SOURCES = ['platform', 'inferred'] as const;
/** How long the hint may be: one sentence of ours, never a run of theirs. */
export const TEMPLATE_HINT_MAX = 90;

const shapeIds = POST_SHAPES.map((s) => s.id) as [string, ...string[]];
const short = z.string().trim().min(1).max(40);

export const soundVibeSchema = z
  .object({
    genre: short,
    tempo: z.enum(TEMPLATE_TEMPOS),
    energy: z.enum(TEMPLATE_ENERGIES),
    /** `platform` when the source named its track, `inferred` when we read it off the mood. */
    source: z.enum(TEMPLATE_SOUND_SOURCES),
  })
  .strict();

export const postTemplateSchema = z
  .object({
    shapeId: z.enum(shapeIds),
    hook: z.enum(TEMPLATE_HOOKS),
    camera: z.enum(TEMPLATE_CAMERAS),
    subjectFocus: z.enum(TEMPLATE_SUBJECTS),
    /** Words for the photo picker, so the property's matching pictures lead. */
    subjectTerms: z.array(short).max(6),
    light: z.enum(TEMPLATE_LIGHTS),
    mood: z.array(z.enum(TEMPLATE_MOODS)).max(3),
    textOnScreen: z.enum(TEMPLATE_TEXT),
    textPosition: z.enum(TEMPLATE_TEXT_POSITIONS),
    lengthClass: z.enum(TEMPLATE_LENGTHS),
    soundVibe: soundVibeSchema.nullable(),
    captionStyle: z
      .object({ hashtags: z.enum(['few', 'many']), opener: z.enum(['hook', 'plain']) })
      .strict(),
    /** The moment box's placeholder: "A hot tub in the snow, like the post you liked". */
    momentHint: z.string().trim().min(1).max(TEMPLATE_HINT_MAX),
  })
  .strict();
export type PostTemplate = z.infer<typeof postTemplateSchema>;
export type SoundVibe = z.infer<typeof soundVibeSchema>;
